import { ProductFilters } from "../types";

/**
 * Reading and writing the Products page's list state as URL query parameters.
 *
 * The filters used to live only in component state, so they were lost the
 * moment the page unmounted -- opening a product and coming back left the user
 * facing the unfiltered list again (GitHub issue #26). Putting them in the URL
 * makes the address bar the single source of truth, which means the browser's
 * own history restores them on Back, and a filtered list can be bookmarked or
 * shared.
 *
 * Only keys listed here round-trip. `ProductFilters` also carries `page` and
 * `limit`, which are request-shaping fields rather than user choices and are
 * handled separately.
 */
export const PRODUCT_FILTER_KEYS = [
  "search",
  "businessArea",
  "division",
  "craStatus",
  "sdlStatus",
  "status",
  "keyProduct",
  "aiFeatures",
  "craComplianceStatus",
  "dataActStatus",
  "lifecycleStatus",
] as const;

export type ProductFilterKey = (typeof PRODUCT_FILTER_KEYS)[number];

/** Query parameters the Products page owns but which are not filters. */
export const PAGE_PARAM = "page";
export const PER_PAGE_PARAM = "perPage";

export const DEFAULT_ITEMS_PER_PAGE = 20;

/**
 * True when any filter is set.
 *
 * Drives whether "Clear Filters" is offered. It is derived from
 * PRODUCT_FILTER_KEYS rather than written out, because the previous hand-listed
 * condition had fallen out of step with the UI: a user who had set only
 * Lifecycle Status was shown no way to clear it.
 */
export function hasActiveFilters(filters: ProductFilters): boolean {
  return PRODUCT_FILTER_KEYS.some((key) => {
    const value = filters[key];
    return typeof value === "string" && value !== "";
  });
}

/** Read the filter set out of a URL's query string. */
export function filtersFromSearchParams(
  params: URLSearchParams,
): ProductFilters {
  const filters: ProductFilters = {};
  for (const key of PRODUCT_FILTER_KEYS) {
    const value = params.get(key);
    if (value) filters[key] = value;
  }
  return filters;
}

/**
 * Return a copy of `filters` with one key set, or removed when the value is
 * empty.
 *
 * Clearing has to delete the key rather than assign undefined: the project
 * compiles with exactOptionalPropertyTypes, so an explicit undefined is not a
 * valid value for an optional field, and it would also serialise as an empty
 * parameter in the URL.
 */
export function withFilter(
  filters: ProductFilters,
  key: ProductFilterKey,
  value: string,
): ProductFilters {
  const next = { ...filters };
  if (value) {
    next[key] = value;
  } else {
    delete next[key];
  }
  return next;
}

/** Read the 1-based page number, defaulting to the first page. */
export function pageFromSearchParams(params: URLSearchParams): number {
  const parsed = Number.parseInt(params.get(PAGE_PARAM) ?? "", 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 1;
}

/** Read the page size, defaulting to DEFAULT_ITEMS_PER_PAGE. */
export function perPageFromSearchParams(params: URLSearchParams): number {
  const parsed = Number.parseInt(params.get(PER_PAGE_PARAM) ?? "", 10);
  return Number.isInteger(parsed) && parsed > 0
    ? parsed
    : DEFAULT_ITEMS_PER_PAGE;
}

/**
 * Write list state back into a query string.
 *
 * Existing parameters the page does not own are preserved -- notably `my`,
 * which selects the "My Products" view and is not a filter. Empty filters and
 * default paging are omitted so that the common case stays a clean `/products`
 * rather than a URL full of empty parameters.
 */
export function applyListStateToSearchParams(
  current: URLSearchParams,
  state: {
    filters: ProductFilters;
    page: number;
    itemsPerPage: number;
  },
): URLSearchParams {
  const next = new URLSearchParams(current);

  for (const key of PRODUCT_FILTER_KEYS) {
    const value = state.filters[key];
    if (typeof value === "string" && value !== "") {
      next.set(key, value);
    } else {
      next.delete(key);
    }
  }

  if (state.page > 1) {
    next.set(PAGE_PARAM, String(state.page));
  } else {
    next.delete(PAGE_PARAM);
  }

  if (state.itemsPerPage !== DEFAULT_ITEMS_PER_PAGE) {
    next.set(PER_PAGE_PARAM, String(state.itemsPerPage));
  } else {
    next.delete(PER_PAGE_PARAM);
  }

  return next;
}

/**
 * The Products URL to return to, as stored in history state when opening a
 * product. Returned with the leading path so it can be handed straight to
 * navigate().
 */
export function productsListUrl(params: URLSearchParams): string {
  const query = params.toString();
  return query ? `/products?${query}` : "/products";
}
